import React from "react";
import { createPortal } from "react-dom";
import { t, getLang, locale } from "../i18n";
import { BugIcon, XIcon } from "./Icons";
import { useModalPresence } from "../utils/modalPresence";
import { useEscapeLayer } from "../utils/escapeLayers";
import UPDATES_LOG from "../updatesLog";

// Книжечка рядом с колокольчиком: короткие справки о том, что нового в
// панели (updatesLog.js), и кнопка «Создать тикет». Открывается так же, как
// уведомления — карточкой под кнопкой на широком экране и листом снизу на
// телефоне, — и теми же классами (.nc-*), чтобы выглядеть одной семьёй.
//
// Кнопки две: в шапке дашборда на компьютере и в листе «Ещё» на телефоне, —
// поэтому открытостью и отметкой «прочитано» управляет App, а здесь — сама
// карточка. Точка на кнопке — пока самая свежая справка не открыта в этом
// браузере.
const SEEN_STORAGE_KEY = "umbrella_updates_seen";
// Слой — как у уведомлений (TOP_RIGHT_PANEL_Z_INDEX в App).
const PANEL_Z_INDEX = 141000;

export const LATEST_UPDATE_ID = UPDATES_LOG[0]?.id || "";

export const readUpdatesSeenId = () => {
  try {
    return String(window.localStorage.getItem(SEEN_STORAGE_KEY) || "");
  } catch (error) {
    return "";
  }
};

export const writeUpdatesSeenId = (id) => {
  try {
    window.localStorage.setItem(SEEN_STORAGE_KEY, String(id || ""));
  } catch (error) {
    // Не сохранилось — точка вернётся после перезагрузки, и только.
  }
};

const pickText = (value) => {
  if (!value || typeof value !== "object") return String(value || "");
  return value[getLang()] || value.ru || "";
};

const formatEntryDate = (date) => {
  const parsed = new Date(`${date}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return String(date || "");
  return parsed.toLocaleDateString(locale(), { day: "numeric", month: "long" });
};

const UpdatesBook = ({ open = false, onClose, anchorRect = null, isPhoneView = false, onCreateTicket }) => {
  const presence = useModalPresence(open, 220);
  const close = () => onClose?.();
  useEscapeLayer(open, close);

  if (!presence.mounted || typeof document === "undefined") return null;

  // Без кнопки-якоря (открыли из листа «Ещё») — листом снизу.
  const asSheet = isPhoneView || !anchorRect;
  const popoverPosition = (() => {
    if (asSheet || typeof window === "undefined") return {};
    const top = Math.max(12, Math.round(Number(anchorRect.bottom) || 0) + 10);
    const right = Math.max(12, Math.round(window.innerWidth - Number(anchorRect.right || 0)));
    return { top: `${top}px`, right: `${right}px`, maxHeight: `min(640px, calc(100vh - ${top + 16}px))` };
  })();

  let lastDate = "";
  return createPortal(
    <>
      <div className="nc-overlay" data-state={presence.state} onClick={close} style={{ zIndex: PANEL_Z_INDEX - 1 }} />
      <div
        className="nc ub"
        role="dialog"
        aria-label={t("Обновления")}
        data-variant={asSheet ? "sheet" : "popover"}
        data-state={presence.state}
        style={{ zIndex: PANEL_Z_INDEX, ...popoverPosition }}
      >
        {asSheet && <div className="nc-handle" />}
        <div className="nc-head">
          <div style={{ minWidth: 0 }}>
            <h3 className="nc-title">{t("Обновления")}</h3>
            <div className="nc-subtitle">{t("Что нового в панели — коротко")}</div>
          </div>
          <div className="nc-head-actions">
            <button type="button" className="nc-icon-btn" onClick={close} aria-label={t("Закрыть")}>
              <XIcon size={15} />
            </button>
          </div>
        </div>

        <div className="nc-list">
          {UPDATES_LOG.map((entry) => {
            const showDate = entry.date !== lastDate;
            lastDate = entry.date;
            return (
              <React.Fragment key={entry.id}>
                {showDate && <div className="nc-section">{formatEntryDate(entry.date)}</div>}
                <div className="ub-item" data-fresh={entry.id === LATEST_UPDATE_ID ? "1" : "0"}>
                  <div className="ub-item-title">{pickText(entry.title)}</div>
                  <div className="ub-item-text">{pickText(entry.text)}</div>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        <div className="nc-foot ub-foot">
          <span className="nc-foot-note">{t("Нашли ошибку? Сообщите нам")}</span>
          <button
            type="button"
            className="ub-ticket-btn"
            onClick={() => {
              close();
              onCreateTicket?.();
            }}
          >
            <BugIcon size={15} />
            {t("Создать тикет")}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default UpdatesBook;
